<template>
  <div class="el-input-mobile-wrapper">
    <div v-if="example" class="example-hint text-right" :style="{ position: 'absolute', right: 0, top: '-25px', }">
      <span class="text-sm sm:text-xs text-accent ml-auto mt-auto">
        <span v-if="country === 'MY'">{{ $t("格式") }}</span>
        <span v-else>{{ $t("範例格式") }}</span>
        <span :style="{ fontWeight: 600 }">{{ example }}</span>
        <span>{{ hint }}</span>
      </span>
    </div>
    <el-input 
      v-model="national" 
      :disabled="props.disabled"
      :minlength="minlength" 
      :maxlength="maxlength" 
      :placeholder="placeholder" 
      :size="size"
      show-word-limit 
      autocomplete="off"
      @blur="handleBlur" 
      @focus="handleFocus" 
      @change="handleChange" 
      @input="handleInput"
      @clear="handleClear"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import parsePhoneNumber, { getCountryCallingCode } from "libphonenumber-js";

const countryMobileData = {
  TW: {
    minlength: 10,
    maxlength: 10,
    example: "0912345678",
    hint: "(共10碼)",
    zeroFirst: true,
  },
  HK: {
    minlength: 8,
    maxlength: 8,
    example: "51234567",
    hint: "(共8碼)",
    zeroFirst: false,
  },
  MY: {
    minlength: 10,
    maxlength: 11,
    example: "0123456789",
    hint: "(共10或11碼)",
    zeroFirst: true,
  },
};

const props = defineProps({
  modelValue: String,
  country: String,
  placeholder: { type: String, required: false, default: '' },
  size: { type: String, required: false, default: '' },
  disabled: Boolean,
});

const e164 = ref(props.modelValue);
const national = ref(props.modelValue);
const idd = getCountryCallingCode(props.country);

watch(() => props.modelValue, (newVal) => {
  e164.value = newVal;
  national.value = newVal;
  e164ToNational();
});

const example = computed(() => countryMobileData[props.country]?.example || "");
const hint = computed(() => countryMobileData[props.country]?.hint || "");
const minlength = computed(() => countryMobileData[props.country]?.minlength || 4);
const maxlength = computed(() => countryMobileData[props.country]?.maxlength || 20);

function e164ToNational() {
  try {
    const phoneNumber = parsePhoneNumber(e164.value, props.country);
    if (phoneNumber.country === props.country) {
      e164.value = phoneNumber.number;
      national.value = countryMobileData[phoneNumber.country]?.zeroFirst
        ? `0${phoneNumber.nationalNumber}`
        : `${phoneNumber.nationalNumber}`;
    } else {
      e164.value = null;
      national.value = null;
    }
  } catch (error) {
    e164.value = null;
    national.value = null;
  }
}

function nationalToE164() {
  try {
    const phoneNumber = parsePhoneNumber(`+${idd}${national.value}`);
    if (phoneNumber?.isValid()) {
      e164.value = phoneNumber.number;
    } else {
      e164.value = null;
      emit('error', phoneNumber?.number);
    }
  } catch (error) {
    e164.value = null;
    emit('error', null);
  }
  emit('input', e164.value);
}

function handleInit() {
  e164ToNational();
  nationalToE164();
}

onMounted(handleInit);

const emit = defineEmits(['input', 'blur', 'focus', 'change', 'clear', 'error']);

function handleBlur(event) {
  emit('blur', event);
}

function handleFocus(event) {
  emit('focus', event);
}

function handleChange(value) {
  emit('change', value);
}

function handleInput(value) {
  national.value = value.replace(/\D/g, '');
  nationalToE164();
}

function handleClear() {
  emit('clear');
}
</script>

<style lang="scss">
.el-input-mobile-wrapper {
  margin: 0;
  padding: 0;
  width: 100%;
}
</style>
